import { Fragment, useRef, useState, type ReactElement } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@dynatrace/strato-components/buttons';
import { DotMenuIcon, DownloadIcon } from '@dynatrace/strato-icons';

import { DataTableV2DownloadData } from './Download/DataTableV2DownloadData.js';
import { type IntentProps } from '../../../../core/slots/Intent/Intent.js';
import { Menu } from '../../../../navigation/menu/Menu.js';
import { Tooltip } from '../../../../overlays/tooltip/Tooltip.js';
import { dataTableMessages } from '../../../DataTableV2/messages.js';

interface ToolbarMenuProps {
  hasDownloadData: boolean;
  intents: Array<IntentProps>;
}

/**
 * The menu component for the `DownloadData` and `Intent` slots.
 *
 * @internal
 */
export const DataTableV2ToolbarMenu = (props: ToolbarMenuProps) => {
  const { hasDownloadData, intents } = props;

  const [menuOpen, setMenuOpen] = useState(false);
  const hasIntents = intents.length > 0;
  const triggerIcon = hasIntents ? <DotMenuIcon /> : <DownloadIcon />;
  const Group = hasIntents ? Menu.Group : Fragment;

  const intl = useIntl();
  const toolbarMenuLabel = intl.formatMessage(
    hasIntents
      ? dataTableMessages.toolbarButtonLabel
      : dataTableMessages.downloadButtonLabel,
  );
  const simpleButtonRef = useRef<HTMLButtonElement>(null);

  // do not render anything if neither the `DownloadData` nor the `Intent` slot is used
  if (!hasDownloadData && !hasIntents) {
    return;
  }

  // Avoid spawning the menu while it is closed to avoid react render
  // performance issues.
  if (menuOpen === false) {
    return (
      <Tooltip text={toolbarMenuLabel}>
        <Button
          ref={simpleButtonRef}
          variant="default"
          aria-label={toolbarMenuLabel}
          onClick={() => setMenuOpen(true)}
        >
          <Button.Prefix>{triggerIcon}</Button.Prefix>
        </Button>
      </Tooltip>
    );
  }

  // according to the GUIDEline intent items with app ids are grouped
  // intent items that don't have an app id are positioned below that group
  const {
    intentsWithAppOrIntentId: intentsWithAppId,
    intentsWithoutAppOrIntentId: intentsWithoutAppId,
  } = intents.reduce<{
    intentsWithAppOrIntentId: ReactElement[];
    intentsWithoutAppOrIntentId: ReactElement[];
  }>(
    (acc, intentProps) => {
      const hasAppOrIntentId =
        intentProps.options.recommendedAppId !== undefined ||
        intentProps.options.recommendedIntentId !== undefined;

      const intent = (
        <Menu.Intent {...intentProps}>
          {intentProps.icon && (
            <Menu.ItemIcon>{intentProps.icon}</Menu.ItemIcon>
          )}
          {intentProps.children}
        </Menu.Intent>
      );

      hasAppOrIntentId
        ? acc.intentsWithAppOrIntentId.push(intent)
        : acc.intentsWithoutAppOrIntentId.push(intent);
      return acc;
    },
    { intentsWithAppOrIntentId: [], intentsWithoutAppOrIntentId: [] },
  );

  return (
    <Menu
      open={menuOpen}
      onOpenChange={(open) => {
        // Since the elements are actually swapped when the menu is open / closed
        // the focus needs to move back to the button only render.
        if (open === false) {
          requestAnimationFrame(() => {
            simpleButtonRef.current?.focus();
          });
        }
        setMenuOpen(open);
      }}
    >
      <Tooltip text={toolbarMenuLabel}>
        <Menu.Trigger>
          <Button variant="default" aria-label={toolbarMenuLabel}>
            <Button.Prefix>{triggerIcon}</Button.Prefix>
          </Button>
        </Menu.Trigger>
      </Tooltip>

      <Menu.Content alignment={'end'}>
        {hasDownloadData && (
          <Group>
            <DataTableV2DownloadData />
          </Group>
        )}
        {intentsWithAppId.length > 0 && <Group>{intentsWithAppId}</Group>}
        {intentsWithoutAppId.length > 0 && <Group>{intentsWithoutAppId}</Group>}
      </Menu.Content>
    </Menu>
  );
};
